/**
 * cart.js - AJAX cart for the "not your fault!" theme.
 *
 * - <cart-drawer> custom element (sections/cart-drawer.liquid, rendered in
 *   the layout) with open/close state that survives re-renders.
 * - Intercepts every product form (`form[action*="/cart/add"]`) and posts it
 *   to /cart/add.js, asking Shopify to return the freshly rendered
 *   cart-drawer section in the same response (Section Rendering API).
 * - Quantity steppers / remove buttons inside the drawer post to
 *   /cart/change.js the same way.
 * - Header cart bubbles ([data-cart-count]) stay in sync everywhere.
 *
 * Progressive enhancement: without JS, product forms post to /cart/add and
 * the header cart icon links to the /cart page.
 */
(() => {
  'use strict';

  const SECTION_ID = 'cart-drawer';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = Number(count) === 0;
    });
  }

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-close]') || e.target.hasAttribute('data-cart-overlay')) {
          this.close();
          return;
        }
        const qtyBtn = e.target.closest('[data-cart-qty]');
        if (qtyBtn && !qtyBtn.disabled) {
          this.changeLine(qtyBtn.dataset.line, qtyBtn.dataset.quantity);
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });
    }

    open() {
      this.classList.add('is-open');
      document.documentElement.classList.add('drawer-open');
      if (window.nyfCoupon && typeof window.nyfCoupon.paint === 'function') {
        window.nyfCoupon.paint(this);
      }
      const panel = this.querySelector('.drawer');
      if (panel) panel.focus({ preventScroll: true });
    }

    close() {
      this.classList.remove('is-open');
      document.documentElement.classList.remove('drawer-open');
    }

    /** Swap the drawer body for a freshly rendered copy of this section. */
    renderSection(html) {
      if (!html) return this.refresh();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-drawer-inner]');
      const current = this.querySelector('[data-drawer-inner]');
      if (fresh && current) current.replaceWith(fresh);
      const freshDrawer = doc.querySelector('cart-drawer');
      if (freshDrawer) {
        this.dataset.itemCount = freshDrawer.dataset.itemCount;
        updateCartCount(freshDrawer.dataset.itemCount);
      }
      if (window.nyfCoupon && typeof window.nyfCoupon.paint === 'function') {
        window.nyfCoupon.paint(this);
      }
    }

    /** Fallback full refresh through the Section Rendering API. */
    async refresh() {
      try {
        const res = await fetch(`${root}?sections=${SECTION_ID}`);
        const data = await res.json();
        this.renderSection(data[SECTION_ID]);
      } catch (err) {
        /* network hiccup - leave the current markup in place */
      }
    }

    async changeLine(line, quantity) {
      this.toggleBusy(true);
      try {
        const res = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: Number(line),
            quantity: Number(quantity),
            sections: SECTION_ID,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || 'cart error');
        this.renderSection(data.sections && data.sections[SECTION_ID]);
      } catch (err) {
        window.themeToast && window.themeToast(this.dataset.textError || 'Something went wrong');
      } finally {
        this.toggleBusy(false);
      }
    }

    toggleBusy(busy) {
      this.querySelectorAll('[data-cart-qty]').forEach((btn) => (btn.disabled = busy));
    }
  }

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  const getDrawer = () => document.querySelector('cart-drawer');

  function fireInitiateCheckout(event) {
    try {
      const node = event.target && event.target.nodeType === 1 ? event.target : event.target && event.target.parentElement;
      if (!node || typeof node.closest !== 'function') return;
      const link = node.closest('[data-checkout-link]');
      if (!link) return;
      if (!window.nyfPixel || typeof window.nyfPixel.track !== 'function') return;
      const value = Number(link.dataset.pixelValue || 0) / 100;
      const count = Number(link.dataset.pixelCount || 0);
      window.nyfPixel.track('InitiateCheckout', {
        value: value,
        currency: window.nyfPixel.currency,
        num_items: count,
        content_type: 'product'
      });
    } catch (err) {
      /* never block checkout */
    }
  }

  document.addEventListener('click', fireInitiateCheckout);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submitter = event.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'checkout') return;
    if (!window.nyfPixel || typeof window.nyfPixel.initiateCheckout !== 'function') return;
    fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((cart) => window.nyfPixel.initiateCheckout(cart))
      .catch(() => {});
  });

  /* ---------- open the drawer from any cart icon ---------- */
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-cart-toggle]');
    if (!toggle) return;
    const drawer = getDrawer();
    if (!drawer) return; // fall through to the /cart link
    e.preventDefault();
    drawer.open();
  });

  /* ---------- AJAX add-to-cart for every product form ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    if (form.hasAttribute('data-no-ajax')) return;

    const drawer = getDrawer();
    if (!drawer) return; // no drawer on this page - let the form post normally

    e.preventDefault();
    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    try {
      const body = new FormData(form);
      body.append('sections', SECTION_ID);
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || 'cart error');

      let pending = '';
      try {
        pending = (sessionStorage.getItem('nyf:pdp-coupon') || '').trim();
      } catch (err) {
        pending = '';
      }
      if (pending) {
        try {
          const discRes = await fetch(`${root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: pending, sections: SECTION_ID }),
          });
          const discData = await discRes.json();
          if (discRes.ok && !discData.status) {
            drawer.renderSection(discData.sections && discData.sections[SECTION_ID]);
          } else {
            drawer.renderSection(data.sections && data.sections[SECTION_ID]);
          }
        } catch (err) {
          drawer.renderSection(data.sections && data.sections[SECTION_ID]);
        }
      } else {
        drawer.renderSection(data.sections && data.sections[SECTION_ID]);
      }
      drawer.open();

      if (window.nyfPixel && typeof window.nyfPixel.addToCart === 'function') {
        window.nyfPixel.addToCart(data);
      }

      const template = drawer.dataset.textAdded || '__PRODUCT__ added';
      window.themeToast && window.themeToast(template.replace('__PRODUCT__', data.product_title || ''));
    } catch (err) {
      window.themeToast && window.themeToast(drawer.dataset.textError || 'Something went wrong');
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  });
})();
